import { SYNC_OP_LABEL, SyncStatusCell } from "@/components/admin/SyncStatusCell";
import { BrandLoader } from "@/components/brand/BrandLoader";
import { EmptyState } from "@/components/brand/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { channelService } from "@/lib/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { RadioTower, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export function ChannelsPage() {
  const {
    data: status,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["channex-status"],
    queryFn: channelService.status,
  });

  const syncMutation = useMutation({
    mutationFn: channelService.sync,
    onSuccess: () => {
      toast.success("전체 동기화가 큐에 등록되었습니다. 잠시 후 상태를 새로고침하세요.");
    },
    onError: () => {
      toast.error("동기화 요청에 실패했습니다. 잠시 후 다시 시도하거나 운영팀에 문의하세요.");
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">채널 연동</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            숙소·객실 타입·요금은 채널 매니저와 양방향 동기화됩니다 — 여기서 등록·수정하면 채널로
            반영되고, 채널 쪽 변경도 동기화 시 반영됩니다. 가용 재고는 PMS가 푸시하고 OTA 예약은
            자동 수신됩니다. OTA 채널 연결·매핑은 운영팀이 진행합니다.
          </p>
        </div>
        <Button
          onClick={() => syncMutation.mutate()}
          disabled={!status?.enabled || syncMutation.isPending}
        >
          <RefreshCw className="h-4 w-4" /> 전체 동기화
        </Button>
      </div>

      {isLoading ? (
        <BrandLoader className="py-12" size={44} label="불러오는 중…" />
      ) : !status ? (
        <Card>
          <EmptyState title="상태를 불러오지 못했습니다" description="잠시 후 다시 시도하세요." />
        </Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <RadioTower className="h-4 w-4" />
                연동 상태
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    status.enabled
                      ? "bg-primary/10 text-primary"
                      : "bg-destructive/10 text-destructive"
                  }`}
                >
                  {status.enabled ? "활성" : "비활성 (API 키 없음)"}
                </span>
                <span className="text-muted-foreground">{status.base_url}</span>
              </div>
              <p className="text-muted-foreground">
                푸시 범위 {status.horizon_days}일 · 예약 피드 폴링{" "}
                {Math.round(status.feed_poll_seconds / 60)}분 간격 (웹훅 백스톱)
              </p>
              <p className="text-muted-foreground">
                {status.alerts_enabled
                  ? `한 방향이 ${status.alert_after_failures}회 연속 실패하면 운영팀에 메일로 알려드려요.`
                  : "동기화 실패 알림 메일은 꺼져 있어요 (메일 발송 설정이 없어요). 이 화면에서 상태를 확인해 주세요."}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">숙소별 채널 매핑</CardTitle>
            </CardHeader>
            <CardContent className="p-0 text-sm">
              {status.properties.length === 0 ? (
                <EmptyState
                  title="연동된 숙소가 없습니다"
                  description="숙소 관리에서 숙소를 등록하면 채널에 연결됩니다."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>숙소</TableHead>
                      <TableHead>상태</TableHead>
                      <TableHead>{SYNC_OP_LABEL.availability}</TableHead>
                      <TableHead>{SYNC_OP_LABEL.content}</TableHead>
                      <TableHead className="text-right">객실 타입</TableHead>
                      <TableHead className="text-right">요금제</TableHead>
                      <TableHead className="text-right">OTA 예약</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {status.properties.map((p) => (
                      <TableRow key={p.property_id}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              p.channex_property_id
                                ? "bg-primary/10 text-primary"
                                : "bg-destructive/10 text-destructive"
                            }`}
                          >
                            {p.channex_property_id ? "연결됨" : "연결 대기"}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">
                          <SyncStatusCell
                            state={p.sync.availability}
                            staleAfterMinutes={status.stale_after_minutes}
                          />
                        </TableCell>
                        <TableCell className="text-xs">
                          <SyncStatusCell
                            state={p.sync.content}
                            staleAfterMinutes={status.stale_after_minutes}
                          />
                        </TableCell>
                        <TableCell className="tabular-nums text-right">
                          {p.mappings.room_type}
                        </TableCell>
                        <TableCell className="tabular-nums text-right">
                          {p.mappings.rate_plan}
                        </TableCell>
                        <TableCell className="tabular-nums text-right">
                          {p.mappings.booking}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <p className="p-4 text-muted-foreground">
                재고·요금 푸시가 실패로 남아 있으면 채널이 옛 재고를 팔 수 있어요 —{" "}
                <b>전체 동기화</b>로 먼저 복구해 주세요. 객실 타입·요금제 매핑이 0일 때도
                같습니다. 상태가 갱신되지 않으면{" "}
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={() => refetch()}
                >
                  새로고침
                </button>
                하세요.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
